import React, { useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import AppControl from "../../../components/";
import Card from "../../../components/controls/Card";
import { createYupSchema } from "../../../utilities/";
import {
  getAssociate,
  saveAssociate,
  updateAssociate,
} from "../../../store/slice/associates";
import {
  getUniquePincodes,
  getUniqueStates,
  searchPincodes,
  getDistrictsByState,
} from "../../../store/slice/pincodes";
import api from "../../../store/client";

const APPLICATION_NAME = "fixilo";

const PAYMENT_HIDDEN_FIELDS = {
  UPI: ["ifsc", "accountNumber"],
  "Bank Account": ["upi"],
};
const FORM_NAMES = {
  EDIT: "EditAssociate",
  CREATE: "Associate",
};
const PAGE_TITLES = {
  EDIT: "EDIT ASSOCIATE",
  CREATE: "CREATE A NEW ASSOCIATE",
};

function PincodeWatcher({ onResults }) {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const prevPincode = useRef(null);
  const prevState = useRef(null);
  const prevPayment = useRef(null);
  const skipNextStateClear = useRef(false);

  useEffect(() => {
    const pincode = values.primaryWorkAreaPinCode;
    if (!pincode || pincode === prevPincode.current) return;
    prevPincode.current = pincode;

    dispatch(searchPincodes(pincode)).then((action) => {
      if (searchPincodes.fulfilled.match(action)) {
        const results = action.payload.data ?? [];
        if (results.length > 0) {
          skipNextStateClear.current = true;
          setFieldValue("state", results[0].state);
          setFieldValue("district", results[0].district);
        }
        onResults(results);
      }
    });
  }, [values.primaryWorkAreaPinCode]);

  useEffect(() => {
    const state = values.state;
    if (!state || state === prevState.current) return;
    prevState.current = state;

    if (skipNextStateClear.current) {
      skipNextStateClear.current = false;
    } else {
      setFieldValue("district", undefined);
    }

    dispatch(getDistrictsByState(state));
  }, [values.state]);

  useEffect(() => {
    const method = values.preferredPaymentMethod;
    if (!method || method === prevPayment.current) return;
    prevPayment.current = method;
    (PAYMENT_HIDDEN_FIELDS[method] ?? []).forEach((field) =>
      setFieldValue(field, undefined),
    );
  }, [values.preferredPaymentMethod]);

  return null;
}

function AssociateAddEdit() {
  const [title, setTitle] = useState(null);
  const [postOfficeOptions, setPostOfficeOptions] = useState([]);
  const { id } = useParams();
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pages = useSelector((state) => state.common.pages);
  const { isLoading, associate } = useSelector((state) => state.associates);
  const uniquePincodes = useSelector((state) => state.pincodes.uniqueList);
  const uniqueStates = useSelector((state) => state.pincodes.uniqueStates);
  const districtsByState = useSelector(
    (state) => state.pincodes.districtsByState,
  );

  const currentPageControls = useMemo(() => {
    if (!pages) return null;
    const formName = id ? FORM_NAMES.EDIT : FORM_NAMES.CREATE;
    const currentPage = pages.find((item) => item.formName === formName);
    return currentPage?.fields;
  }, [pages, id]);

  useEffect(() => {
    dispatch(getUniquePincodes());
    dispatch(getUniqueStates());
    if (id) {
      setTitle(PAGE_TITLES.EDIT);
      dispatch(getAssociate(id));
    } else {
      setTitle(PAGE_TITLES.CREATE);
    }
  }, [id, dispatch]);

  const { initialValues, validationSchema } = useMemo(() => {
    if (!currentPageControls) {
      return { initialValues: {}, validationSchema: Yup.object().shape({}) };
    }

    const coerce = (validationType, value) => {
      switch (validationType) {
        case "boolean":
          return (
            value === true || value === 1 || value === "true" || value === "1"
          );
        case "number":
          return value !== "" && value != null ? Number(value) : "";
        case "array":
          return Array.isArray(value) ? value : [];
        default:
          return value ?? "";
      }
    };

    let values = {};
    if (id && associate) {
      values = currentPageControls.reduce(
        (acc, control) => ({
          ...acc,
          [control.fieldId]: coerce(
            control.validationType,
            associate[control.fieldId],
          ),
        }),
        {},
      );
    } else {
      values = currentPageControls.reduce(
        (acc, control) => ({
          ...acc,
          [control.fieldId]: coerce(
            control.validationType,
            control.defaultValue,
          ),
        }),
        {},
      );
    }

    const yupSchema = currentPageControls.reduce(createYupSchema, {});
    const schema = Yup.object().shape(yupSchema);

    return { initialValues: values, validationSchema: schema };
  }, [currentPageControls, associate, id]);
  const allPincodeOptions = useMemo(
    () => uniquePincodes.map((p) => ({ label: p, value: p })),
    [uniquePincodes],
  );

  const allStateOptions = useMemo(
    () => uniqueStates.map((s) => ({ label: s, value: s })),
    [uniqueStates],
  );

  const allDistrictOptions = useMemo(
    () => districtsByState.map((d) => ({ label: d, value: d })),
    [districtsByState],
  );

  const getDropdownValues = (primarySelected) => ({
    primaryWorkAreaPinCode: allPincodeOptions,
    additionalWorkAreaPinCodes: allPincodeOptions.filter(
      (p) => p.value !== primarySelected,
    ),
    state: allStateOptions,
    district: allDistrictOptions,
    postOffice: postOfficeOptions.map((r) => ({
      label: r.name,
      value: r.name,
    })),
  });
  const handleSubmit = async (values) => {
    const imageFields = (currentPageControls ?? []).filter(
      (c) => c.type === "image",
    );
    const imageFieldIds = new Set(imageFields.map((c) => c.fieldId));

    // Strip File objects from save payload — images upload separately after save
    const saveValues = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [
        k,
        imageFieldIds.has(k) ? (typeof v === "string" ? v : undefined) : v,
      ]),
    );

    const submitData = {
      ...saveValues,
      applicationName: APPLICATION_NAME,
      ...(id && { id: associate.id }),
    };

    // Step 1: save associate
    const thunkCreator = id ? updateAssociate : saveAssociate;
    const resultAction = await dispatch(thunkCreator(submitData));

    if (!thunkCreator.fulfilled.match(resultAction)) {
      const errorMessage =
        resultAction.payload?.message || resultAction.error?.message;
      message.error(`Save failed: ${errorMessage}`);
      return;
    }

    // Step 2: upload images to associate-specific endpoints
    const savedId = id ?? resultAction.payload.data?.id;
    const uploadErrors = [];

    for (const control of imageFields) {
      const value = values[control.fieldId];
      if (!value) continue;

      const fieldExtraProps = (() => {
        try {
          return control.props ? JSON.parse(control.props) : {};
        } catch {
          return {};
        }
      })();
      const uploadType = fieldExtraProps.uploadType ?? control.fieldId;
      const endpoint = `identity/associate/${savedId}/upload/${uploadType}`;

      const uploadFile = async (file) => {
        const data = new FormData();
        data.append("file", file);
        await api.post(endpoint, data, {
          headers: { "content-type": "multipart/form-data" },
        });
      };

      try {
        if (value instanceof File) {
          await uploadFile(value);
        } else if (Array.isArray(value)) {
          await Promise.all(
            value.filter((v) => v instanceof File).map(uploadFile),
          );
        }
      } catch (err) {
        uploadErrors.push(`${control.label}: ${err}`);
      }
    }

    if (uploadErrors.length) {
      message.warning(
        `Saved, but some uploads failed:\n${uploadErrors.join("\n")}`,
      );
    } else {
      message.success(resultAction.payload.message);
    }
    navigate("/associates");
  };

  const renderFormFields = (formik) => {
    const dropdownValues = getDropdownValues(
      formik.values.primaryWorkAreaPinCode,
    );
    const hiddenFields =
      PAYMENT_HIDDEN_FIELDS[formik.values.preferredPaymentMethod] ?? [];
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {currentPageControls
          ?.filter((c) => !hiddenFields.includes(c.fieldId))
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((control) => {
            const extraProps = (() => {
              if (!control.props) return {};
              try {
                return JSON.parse(control.props);
              } catch {
                return {};
              }
            })();
            return (
              <div
                key={control.fieldId}
                className={
                  (control.colspan ?? 1) >= 2 ? "col-span-1 lg:col-span-2" : "col-span-1"
                }
              >
                <AppControl
                  control={control.type}
                  label={control.label}
                  name={control.fieldId}
                  placeholder={control.placeholder}
                  options={dropdownValues[control.fieldId] ?? control.options}
                  defaultValue={control.defaultValue}
                  {...extraProps}
                />
              </div>
            );
          })}
      </div>
    );
  };

  const renderFormActions = () => (
    <div className="border-t-2 border-neutral-100 pt-3">
      <AppControl
        control="button"
        type="submit"
        name="Save"
        size="md"
        color="orange"
        loading={isLoading}
      />
      <AppControl
        control="button"
        type="reset"
        name="Cancel"
        size="md"
        color="blue"
        onClick={() => navigate("/associates")}
      />
    </div>
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Card title={title}>
          <Form>
            <PincodeWatcher onResults={setPostOfficeOptions} />
            {renderFormFields(formik)}
            {renderFormActions()}
          </Form>
        </Card>
      )}
    </Formik>
  );
}

export default AssociateAddEdit;
